if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      if (!this.form) return;

      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.disabled = false;

      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.boundVariantChangeHandler = this.syncVariantIdFromPicker.bind(this);
      this.variantPicker = null;
      this.bindVariantSync();
      this.form.addEventListener('change', this.boundVariantChangeHandler);
    }

    disconnectedCallback() {
      if (this.variantPicker) {
        this.variantPicker.removeEventListener('change', this.boundVariantChangeHandler);
      }
      if (this.form) {
        this.form.removeEventListener('change', this.boundVariantChangeHandler);
      }
    }

    getSectionIdFromForm() {
      if (!this.form || !this.form.id) return null;
      const prefix = 'product-form-';
      if (!this.form.id.startsWith(prefix)) return null;
      return this.form.id.slice(prefix.length);
    }

    getVariantPicker() {
      const localRoot = this.closest('product-info') || this.closest('.product') || this;
      const localPicker = localRoot.querySelector('variant-radios, variant-selects');
      if (localPicker) return localPicker;

      const sectionId = this.getSectionIdFromForm();
      const section = sectionId ? document.querySelector(`[data-section="${sectionId}"]`) : this.closest('section');
      if (!section) return null;

      if (sectionId) {
        const scopedPicker = section.querySelector(
          `variant-radios[data-section="${sectionId}"], variant-selects[data-section="${sectionId}"]`
        );
        if (scopedPicker) return scopedPicker;
      }

      return section.querySelector('variant-radios, variant-selects');
    }

    getVariantData(variantPicker) {
      if (!variantPicker) return null;

      if (typeof variantPicker.getVariantData === 'function') {
        const pickerVariantData = variantPicker.getVariantData();
        if (Array.isArray(pickerVariantData)) return pickerVariantData;
      }

      const variantDataElement = variantPicker.querySelector('script[type="application/json"]');
      if (!variantDataElement) return null;

      try {
        return JSON.parse(variantDataElement.textContent);
      } catch (error) {
        console.error('Invalid variant JSON', error);
        return null;
      }
    }

    getSelectedOptionsFromFormControls() {
      if (!this.form || !this.form.id) return [];

      const formId = window.CSS && CSS.escape ? CSS.escape(this.form.id) : this.form.id;
      const radioInputs = Array.from(document.querySelectorAll(`input[type="radio"][form="${formId}"]`));
      if (radioInputs.length) {
        const radioGroups = new Map();
        radioInputs.forEach((input) => {
          if (!radioGroups.has(input.name)) {
            radioGroups.set(input.name, []);
          }
          radioGroups.get(input.name).push(input);
        });

        const selectedOptions = [];
        for (const inputs of radioGroups.values()) {
          const checked = inputs.find((input) => input.checked);
          if (!checked) return [];
          selectedOptions.push(String(checked.value).trim());
        }
        return selectedOptions;
      }

      return Array.from(document.querySelectorAll(`select[form="${formId}"]`))
        .map((select) => String(select.value).trim())
        .filter((value) => value !== '');
    }

    getSelectedOptionsFromPicker(variantPicker) {
      if (!variantPicker) return [];

      if (variantPicker.tagName === 'VARIANT-RADIOS') {
        return Array.from(variantPicker.querySelectorAll('fieldset'))
          .map((fieldset) => {
            const checked =
              fieldset.querySelector('input[type="radio"]:checked') ||
              fieldset.querySelector('input[type="radio"][checked]');
            return checked ? String(checked.value).trim() : null;
          })
          .filter((value) => value !== null);
      }

      return Array.from(variantPicker.querySelectorAll('select'))
        .map((select) => String(select.value).trim())
        .filter((value) => value !== '');
    }

    normalizeVariantValue(value) {
      return String(value == null ? '' : value).trim();
    }

    resolveSelectedVariant(variantPicker = this.getVariantPicker()) {
      if (!variantPicker) return null;

      const variantData = this.getVariantData(variantPicker);
      const selectedOptions = this.getSelectedOptionsFromFormControls();
      const normalizedSelectedOptions = selectedOptions.length
        ? selectedOptions
        : this.getSelectedOptionsFromPicker(variantPicker);

      if (!variantData || !normalizedSelectedOptions.length) return variantPicker.currentVariant || null;

      const matchedVariant = variantData.find((variant) => {
        const variantOptions =
          Array.isArray(variant.options) && variant.options.length
            ? variant.options
            : [variant.option1, variant.option2, variant.option3].filter(
                (option) => option !== null && option !== undefined && option !== ''
              );

        if (variantOptions.length !== normalizedSelectedOptions.length) return false;

        return normalizedSelectedOptions.every((selectedOption, index) => {
          return this.normalizeVariantValue(variantOptions[index]) === this.normalizeVariantValue(selectedOption);
        });
      });

      return matchedVariant || variantPicker.currentVariant || null;
    }

    syncVariantIdFromPicker() {
      const idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      if (!idInput) return null;

      const variantPicker = this.getVariantPicker();
      const variant = this.resolveSelectedVariant(variantPicker);
      if (variant && variant.id) {
        const nextId = String(variant.id);
        if (idInput.value !== nextId) {
          idInput.value = nextId;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return variant;
      }

      return null;
    }

    bindVariantSync() {
      const nextPicker = this.getVariantPicker();
      if (!nextPicker || this.variantPicker === nextPicker) return;

      if (this.variantPicker) {
        this.variantPicker.removeEventListener('change', this.boundVariantChangeHandler);
      }

      this.variantPicker = nextPicker;
      this.variantPicker.addEventListener('change', this.boundVariantChangeHandler);
      this.syncVariantIdFromPicker();
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      // Force sync selected variant to the hidden product id input before add-to-cart
      this.bindVariantSync();
      const selectedVariant = this.syncVariantIdFromPicker();
      const idInput = this.form.querySelector('input[name="id"]');
      const selectedVariantId = selectedVariant && selectedVariant.id ? String(selectedVariant.id) : '';
      const fallbackVariantId = idInput && idInput.value && /^\d+$/.test(String(idInput.value)) ? String(idInput.value) : '';
      const variantIdToAdd = selectedVariantId || fallbackVariantId;

      if (!variantIdToAdd) {
        this.handleErrorMessage(window.variantStrings?.unavailable || 'Selected variant is unavailable.');
        return;
      }

      if (idInput) {
        idInput.disabled = false;
        idInput.value = variantIdToAdd;
      }

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      formData.set('id', variantIdToAdd);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form'});
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
          this.handleErrorMessage(window.cartStrings?.error || window.variantStrings?.unavailable || 'An error occurred. Please try again.');
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
